import { inject, injectable } from 'inversify';
import {
  GetLocationAddressesRequestParams,
  LocationServiceInterface,
  GetLocationAddressResponse,
  Result,
  AddressComponentType,
} from './locationService.types';
import { gmapsKey } from 'src/config';
import HttpClient from 'src/features/http';

const MAPS_BASE_URL = `https://maps.googleapis.com/maps/api/geocode/json`;

@injectable()
class LocationService implements LocationServiceInterface {
  @inject(HttpClient) private client!: HttpClient;

  compareResults(a: Result, b: Result): number {
    const requiredTypes: AddressComponentType[] = [
      'street_number',
      'route',
      'locality',
      'postal_code',
      'neighborhood',
    ];

    const countRequiredTypes = (result: Result) =>
      requiredTypes.filter((type) =>
        result.address_components.some((component) =>
          component.types.includes(type)
        )
      ).length;

    const aRequiredCount = countRequiredTypes(a);
    const bRequiredCount = countRequiredTypes(b);

    if (aRequiredCount !== bRequiredCount) {
      return bRequiredCount - aRequiredCount;
    }

    return b.address_components.length - a.address_components.length;
  }

  async getLocationAddress(params: GetLocationAddressesRequestParams) {
    const queryParams: Record<string, string> = { key: gmapsKey };
    if (params.placeId) {
      queryParams.place_id = params.placeId;
    } else if (!!params.lat && !!params.lng) {
      queryParams.latlng = `${params.lat},${params.lng}`;
    } else {
      throw new Error('Debe proporcionar lat/lng o placeId');
    }

    const response = await this.client
      .getClient()
      .get<GetLocationAddressResponse>(MAPS_BASE_URL, { params: queryParams });

    const sortedResults = response.data.results.sort(this.compareResults);

    return {
      ...response,
      data: {
        ...response.data,
        results: sortedResults,
      },
    };
  }

  async getLocationAddressByQuery(formattedAddress: string) {
    const queryParams: Record<string, string> = {
      key: gmapsKey,
      address: formattedAddress,
    };
    try {
      const response = await this.client
        .getClient()
        .get<GetLocationAddressResponse>(MAPS_BASE_URL, {
          params: queryParams,
        });

      const sortedResults = response.data.results.sort(this.compareResults);
      return {
        ...response,
        data: {
          ...response.data,
          results: sortedResults,
        },
      };
    } catch (error) {
      console.error(error);
    }
  }
}

export default LocationService;
